import { Zap, Users, Mic, Briefcase, UserPlus, Building2, LayoutGrid } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { CapUsageWidget } from './CapUsageWidget';
import { AgencyClientsCapWidget } from './AgencyClientsCapWidget';
import { AgencyJobsCapWidget } from './AgencyJobsCapWidget';
import { AgencyTranscriptsWidget } from './AgencyTranscriptsWidget';
import {
  getCandidatesLimit,
  getCandidatesRemainingDisplay,
} from '@/lib/billingCandidatesPool';

interface CapUsageWidgetsProps {
  /** When false, hides the plan-level section (useful when shown in separate tab) */
  showPlanSection?: boolean;
}

/**
 * Dashboard "Usage this month" section.
 *
 * v3.5 (25/05/26 — Split Pools):
 * - Drop `aiUser` widget e tutto il dead code relativo (pool eliminato)
 * - Drop dipendenza da useBillingUsageMe (endpoint deprecated)
 * - Mostra SEMPRE due widget distinti: "Candidati elaborati" + "AI Credits"
 *   (i due pool sono ora separati e indipendenti)
 * - aiCompany + candidates: Autumn balances (150/500/1500 AI; 300/1500/5000 candidati)
 *
 * Layout:
 * 1. Plan-level metrics (Agency owners only): clients, jobs, transcripts
 * 2. Company-specific metrics: AI Credits, Candidates, positions, seats
 */
export function CapUsageWidgets({ showPlanSection = true }: CapUsageWidgetsProps) {
  const { t } = useLanguage();
  const { user, selectedCompany } = useAuth();
  const { data: status, isLoading, error, refetch } = useBillingStatus();

  const isAgencyOwner = user?.accountType === 'agency_owner' && !!user.agency;

  if (isLoading) {
    return (
      <section aria-label={t('usage.section_title')} className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t('usage.section_title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map(i => (
            <Card key={i} className="p-4 h-[112px] animate-pulse bg-muted/30 border-border/50" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !status) {
    return (
      <section aria-label={t('usage.section_title')} className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t('usage.section_title')}</h2>
        <Card className="p-4 flex items-center justify-between gap-3 border-border/60">
          <span className="text-sm text-muted-foreground">{t('usage.load_error')}</span>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            {t('usage.retry')}
          </Button>
        </Card>
      </section>
    );
  }

  const planId = status.planId ?? 'freemium_trial';
  const limits = status.limits;
  const balances = status.balances ?? {};

  type BInfo = { granted: number; remaining: number; unlimited: boolean };
  const get = (k: string): BInfo | undefined =>
    (balances as Record<string, BInfo>)[k];

  const aiCompany = get('ai_company');
  const candidates = get('candidates');
  const transcripts = get('transcripts');

  // v3.5: AI Credits cap (150/500/1500) — Autumn `ai_company` pool
  const aiCompanyLimit = limits?.aiCompany ?? 0;
  const aiCompanyUsed = (aiCompany?.granted ?? 0) - (aiCompany?.remaining ?? 0);
  const aiCompanyRemainingDisplay = Math.max(0, aiCompanyLimit - aiCompanyUsed);
  const aiCompanyNotIncluded =
    (limits?.aiCompany ?? 0) === 0 && (!aiCompany || aiCompany.granted === 0);

  // v3.5.2: Candidates cap — Autumn `balances.candidates` (same as AI Credits)
  const candidatesLimit = getCandidatesLimit(status);
  const candidatesRemainingDisplay = getCandidatesRemainingDisplay(status);

  // Transcripts dedicato (Agency Pro only — di solito assorbito in ai_company su SME)
  const transcriptsLimit = limits?.transcripts ?? 0;
  const transcriptsUsed = (transcripts?.granted ?? 0) - (transcripts?.remaining ?? 0);
  const transcriptsRemainingDisplay = Math.max(0, transcriptsLimit - transcriptsUsed);
  const isAgency = planId.startsWith('agency_');
  const showTranscripts = isAgency && transcriptsLimit > 0;

  // Structural counters (positions + seats)
  const positionsCounter = status.structural?.positions;
  const seatsCounter = status.structural?.seats;
  const showPositions = !!positionsCounter && positionsCounter.limit > 0;
  const showSeats = !!seatsCounter && seatsCounter.limit > 0;

  const companyName = selectedCompany?.name ?? t('usage.selected_company');

  const renderCompanyWidgets = () => (
    <>
      <CapUsageWidget
        icon={Zap}
        label={t('usage.ai_company')}
        granted={aiCompanyLimit}
        remaining={aiCompanyRemainingDisplay}
        unlimited={aiCompany?.unlimited || status.isUnlimited}
        planId={planId}
        capKey="aiCompany"
        notIncluded={aiCompanyNotIncluded}
      />
      <CapUsageWidget
        icon={Users}
        label={t('usage.candidates')}
        granted={candidatesLimit}
        remaining={candidatesRemainingDisplay}
        unlimited={candidates?.unlimited || status.isUnlimited}
        planId={planId}
        capKey="candidates"
      />
      {showTranscripts && (
        <CapUsageWidget
          icon={Mic}
          label={t('usage.transcripts')}
          granted={transcriptsLimit}
          remaining={transcriptsRemainingDisplay}
          unlimited={transcripts?.unlimited || status.isUnlimited}
          planId={planId}
          capKey="transcripts"
        />
      )}
      {showPositions && (
        <CapUsageWidget
          icon={Briefcase}
          label={t('usage.positions')}
          granted={positionsCounter!.limit}
          remaining={Math.max(0, positionsCounter!.limit - positionsCounter!.current)}
          unlimited={status.isUnlimited}
          planId={planId}
          capKey="positions"
        />
      )}
      {showSeats && (
        <CapUsageWidget
          icon={UserPlus}
          label={t('usage.seats')}
          granted={seatsCounter!.limit}
          remaining={Math.max(0, seatsCounter!.limit - seatsCounter!.current)}
          unlimited={status.isUnlimited}
          planId={planId}
          capKey="seats"
        />
      )}
    </>
  );

  return (
    <section aria-label={t('usage.section_title')} className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground">{t('usage.section_title')}</h2>

      {/* Plan-level metrics — Agency owners only, in a distinct card.
          Hidden when showPlanSection=false (e.g., when using tabs). */}
      {isAgencyOwner && showPlanSection && (
        <Card className="border-primary/20 bg-primary/[0.02]">
          <CardHeader className="pb-3 pt-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <LayoutGrid className="h-4 w-4 text-primary" />
              {t('usage.plan_level')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              <AgencyClientsCapWidget />
              <AgencyJobsCapWidget />
              <AgencyTranscriptsWidget />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company-specific metrics — wrapper card per agency, plain grid altrimenti */}
      {isAgencyOwner && showPlanSection ? (
        <Card className="border-muted-foreground/20">
          <CardHeader className="pb-3 pt-4">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {companyName}
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {renderCompanyWidgets()}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {renderCompanyWidgets()}
        </div>
      )}
    </section>
  );
}

export default CapUsageWidgets;
